import TextEditor from 'components/text-editor';
import { useAuth } from 'context/AuthContext';
import { useCallback, useEffect, useRef, useState } from 'react';
import { IoIosClose } from 'react-icons/io';
import { toast } from 'react-toastify';
import { sendNotification } from 'services/notifications.service';
import { stripHtmlTags } from 'utils/helpers';

interface Props {
  onClose: () => void;
  managerId: string;
  managerName: string;
  participantCount?: number;
  taskTitle?: string;
}

const useFocusTrap = (ref: React.RefObject<HTMLDivElement>, isOpen: boolean, onClose: () => void) => {
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab' || !ref.current) return;
      event.preventDefault();

      const focusableElements = ref.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusableElements || focusableElements.length === 0) return;

      const focusableArray = Array.from(focusableElements) as HTMLElement[];
      const currentIndex = focusableArray.indexOf(document.activeElement as HTMLElement);

      let nextIndex: number;
      // Se o elemento ativo não está na lista (currentIndex === -1), começa pelo primeiro
      if (currentIndex === -1) {
        nextIndex = 0;
      } else if (event.shiftKey) {
        nextIndex = currentIndex <= 0 ? focusableArray.length - 1 : currentIndex - 1;
      } else {
        nextIndex = currentIndex >= focusableArray.length - 1 ? 0 : currentIndex + 1;
      }

      focusableArray[nextIndex]?.focus();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, ref, onClose]);
};

export default function ModalSendMessage({ onClose, managerId, managerName, participantCount, taskTitle }: Props) {
  const [value, setValue] = useState('');
  const [messageLength, setMessageLength] = useState<{ textLength: number; fullLength: number } | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const { userInfo } = useAuth();
  const modalContentRef = useRef<HTMLDivElement>(null);

  const minTextLength = 10;

  const handleFocusTrapClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useFocusTrap(modalContentRef, true, handleFocusTrapClose);

  function handleChangeMessage(content: string, contentLength: any) {
    setMessageLength(contentLength);
    setValue(content);
  }

  async function handleSendRequest() {
    if (!value || !messageLength || messageLength.textLength < minTextLength) {
      return toast('Você precisa inserir uma justificativa com pelo menos 10 caracteres.', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        type: 'error',
      });
    }

    setIsLoading(true);

    try {
      const message = `
        <div>
          <h3><strong>Solicitação de Aprovação de Participantes</strong></h3>
          <p><strong>Solicitante:</strong> ${userInfo?.nome_guerra || 'Não informado'} (${userInfo?.chave || 'Não informado'})</p>
          <p><strong>Realização:</strong> ${taskTitle || 'Não informado'}</p>
          <p><strong>Número de participantes:</strong> ${participantCount || 'Não informado'}</p>
          <p><strong>Justificativa:</strong></p>
          <div>
            ${value}
          </div>
          <br>
          <p><em>Esta solicitação requer sua aprovação para prosseguir com a realização.</em></p>
        </div>
      `;

      const payload = {
        target: managerId,
        message: stripHtmlTags(value),
        created: { $date: new Date().getTime() },
        sender: userInfo?._id || 'Não informado',
        taskTitle: taskTitle || 'Não informado',
        participantCount: participantCount || 0,
        senderName: userInfo?.nome_guerra || 'Não informado',
      };

      await sendNotification(payload);

      toast('Solicitação enviada com sucesso!', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        type: 'success',
      });

      onClose();
    } catch (error) {
      toast('Erro ao enviar solicitação. Tente novamente.', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  }

  // const handleInit = (evt: any, editor: any) => {
  //   //A method for retrieving the character count in the textarea
  //   setLength(editor.getContent({ format: 'text' }).length);
  // };

  // const handleUpdate = (value: any) => {
  //   const _value = stripHtmlTags(value);

  //   if (_value.length <= sizeLimit) {
  //     setValue(value);
  //     setLength(_value.length);
  //   } else {
  //     setValue(_value.slice(0, sizeLimit));
  //     setLength(_value.slice(0, sizeLimit).length);
  //   }
  // };

  // const handleBeforeAddUndo = (evt: any, editor: any) => {
  //   const length = editor.getContent({ format: 'text' }).length;
  //   // note that this is the opposite test as in handleUpdate
  //   // because we are determining when to deny adding an undo level
  //   if (length > sizeLimit) {
  //     evt.preventDefault();
  //   }
  // };

  return (
    <div className="user-tutorial fixed bg-black bg-opacity-40 top-0 left-0 w-screen h-screen flex justify-center items-start pt-8 pb-8 px-4 overflow-y-auto">
      <div
        ref={modalContentRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-send-message-title"
        className="relative border-2 border-solid border-electric-blue bg-oxford-blue rounded-3xl p-6 w-full max-w-[600px] min-h-fit my-auto"
      >
        <button
          color="primary"
          className="hover:outline outline-white absolute -right-3 -top-3 rounded-[50%] overflow-hidden w-10 h-10 p-0 flex justify-center items-center z-10 bg-purple border-2 border-purple"
          onClick={() => onClose()}
          aria-label="Fechar modal"
        >
          <IoIosClose color="white" size={30} aria-hidden="true" />
        </button>

        <div className="flex flex-col">
          {/* Header */}
          <div className="mb-4 flex-shrink-0">
            <p tabIndex={0} id="modal-send-message-title" className="block text-xl text-center text-yellow font-semibold leading-none mt-2 mb-7">
              Solicitar Aprovação de Participantes
            </p>
            <div className="bg-maastricht-blue-3 bg-opacity-20 rounded-lg p-4 mb-4 ">
              <p tabIndex={0} className="text-sm text-white mb-2">
                <strong className="text-white">Gestor:</strong> {managerName}
              </p>
              {participantCount && (
                <p tabIndex={0} className="text-sm text-white mb-2">
                  <strong className="text-white">Participantes:</strong> {participantCount}
                </p>
              )}
              {taskTitle && (
                <p tabIndex={0} className="text-sm text-white font-medium">
                  <strong className="text-white">Realização:</strong> {taskTitle}
                </p>
              )}
            </div>
            <p tabIndex={0} className="text-sm text-alice-blue">Explique os motivos para essa quantidade de participantes:</p>
          </div>

          {/* Editor Area */}
          <div className="w-full bg-transparent mb-4 h-[250px]">
            <TextEditor onChange={handleChangeMessage} minLength={minTextLength} disableEmoji={true} />
          </div>

          {/* Footer */}
          <div className="flex-shrink-0">
            {/* <small className="block w-full mb-4 text-right font-light text-xs text-alice-blue">
              Mínimo: {minTextLength} caracteres
              {messageLength && (
                <span className={messageLength.textLength < minTextLength ? 'text-red-400' : 'text-green-400'}>
                  {' '}({messageLength.textLength} digitados)
                </span>
              )}
            </small> */}

            <div className="flex justify-center space-x-3">
              {/* <button
                onClick={onClose}
                className="leading-none border-2 border-purple flex items-center justify-center bg-transparent text-center text-white rounded py-3 px-6 hover:bg-purple hover:bg-opacity-10 transition-all"
              >
                <p className="font-semibold text-sm leading-none uppercase">Cancelar</p>
              </button> */}

              <button
                disabled={!value || !messageLength || messageLength.textLength < minTextLength || isLoading}
                onClick={handleSendRequest}
                className={`leading-none disabled:opacity-50 border-2 border-purple flex items-center justify-center bg-purple text-center text-white rounded py-3 px-6 hover:bg-purple-600 transition-all ${isLoading ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <p className="font-semibold text-sm leading-none uppercase">
                  {isLoading ? 'Enviando...' : 'Solicitar Autorização'}
                </p>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


